import React, { useEffect, useRef } from "react";

// SASS
import "./Part7.scss";

// COMPONENTS
import Step from "../Step/Step";

// HELPERS
import animate from "../../utils/animate";

// LIBRARIES
import scrollama from "scrollama";

// ASSETS
import orangeGermImg from "../../assets/img/germ-4.svg";
import orangeGerm2Img from "../../assets/img/germ-6-orange.svg";
import darkBlueGermImg from "../../assets/img/germ-5.svg";
import purplegermImg from "../../assets/img/germ-3.svg";
import turquoiseGermImg from "../../assets/img/germ-1.svg";
import lightBlueGermImg from "../../assets/img/germ-2.svg";
import bubbleImg from "../../assets/img/bubble.png";

export default function Part({ step, setShowScrolldown }) {
  // SCROLLAMA INSTANCE
  const scrolly = scrollama();

  // ANIMATION STEP DATA
  const animationSequence = [
    { stepNumber: "0", height: null, description: "spacer" },
    {
      stepNumber: "1",
      height: null,
      description: "fade germs container 1, bold top text span",
    },
    {
      stepNumber: "2",
      height: null,
      description: "fade germs container 2, change bg color, fade in bubble",
    },
    { stepNumber: "3", height: null, description: "fade in bottom text" },
    { stepNumber: "4", height: null, description: "spacer", offset: 1},
  ];

  // REFS
  const germContainer1El = useRef(null);
  const germContainer2El = useRef(null);
  const topRowEl = useRef(null);
  const topTextBoldEl = useRef(null);
  const bottomTextEl = useRef(null);
  const bubbleContainerEl = useRef(null);
  const bottomRowTextEl = useRef(null);
  const backgroundEl = useRef(null);

  // RUN WHEN THE COMPONENT MOUNTS
  useEffect(() => {
    scrolly
      .setup({
        step: `.${step}`,
        debug: false,
        threshold: 1,
        progress: true,
      })
      .onStepEnter(({ element, direction }) => {
        const step = element.dataset.step;
        switch (step) {
          case "1": {
            animate.fadeOutOnEnter(germContainer2El.current, direction);
            animate.addClassOnEnter(topTextBoldEl.current, "bold", direction);
            break;
          }
          case "2": {
            animate.fadeOutOnEnter(germContainer1El.current, direction);
            animate.fadeInOnEnter(bubbleContainerEl.current, direction);
            //animate.addClassOnEnter(topRowEl.current, "fade", direction);
            animate.fadeInOnEnter(bottomTextEl.current, direction);
            break;
          }
          case "3": {
            animate.fadeInOnEnter(bottomRowTextEl.current, direction);
            break;
          }
          case "4": {
            if (direction === "down") setShowScrolldown(false);
            break;
          }
          default:
            break;
        }
      })
      .onStepProgress(({ element, progress }) => {
        const step = element.dataset.step;
        switch (step) {
          case "1": {
            backgroundEl.current.style.opacity = 1 - progress / 2;
            break;
          }
          case "2": {
            backgroundEl.current.style.opacity = 0.5 - progress / 2;
            break;
          }
          default:
            break;
        }
      })
      .onStepExit(({ element, direction }) => {
        const step = element.dataset.step;
        switch (step) {
          case "1": {
            animate.fadeInOnExit(germContainer2El.current, direction);
            animate.removeClassOnExit(topTextBoldEl.current, "bold", direction);
            break;
          }
          case "2": {
            animate.fadeInOnExit(germContainer1El.current, direction);
            animate.fadeOutOnExit(bubbleContainerEl.current, direction);
            animate.fadeOutOnExit(bottomTextEl.current, direction);
            break;
          }
          case "3": {
            animate.fadeOutOnExit(bottomRowTextEl.current, direction);
            break;
          }
          case "4": {
            if (direction === "up") setShowScrolldown(true);
            break;
          }
          default:
            break;
        }
      });
  }, [scrolly, step]);

  // CUSTOM FUNCTIONS

  return (
    <section className="wrapper">
      <div
        className="sticky-container"
        style={{ height: "100vh", transform: "unset", top: 0 }}
      >
        {/* STICKY */}
        <div className="part7">
          <div className="top-row" ref={topRowEl}>
            <div className="background" ref={backgroundEl}></div>
            <div className="text text-top">
              So, we can see why good hand{" "}
              <span className="bold" ref={topTextBoldEl}>
                hygiene, cleaning, and disinfection
              </span>{" "}
              are important.
            </div>
            <div className="text text-bottom" ref={bottomTextEl}>
              These actions make sure we can reduce the spread of viruses and
              other germs that can make us sick.
            </div>
          </div>
          <div className="bottom-row">
            <div className="text" ref={bottomRowTextEl}>
              We encourage you to apply this knowledge to other diseases—not
              just COVID-19—to protect yourself and others from sickness.
            </div>
          </div>
          <div
            className="germs-container germs-container-1"
            ref={germContainer1El}
          >
            <div className="germ germ-1 rotate delay-random-1">
              <img src={purplegermImg} alt="Germ" className="img-fluid" />
            </div>
            <div className="germ germ-2 rotate delay-random-2">
              <img src={darkBlueGermImg} alt="Germ" className="img-fluid" />
            </div>
            <div className="germ germ-3 rotate delay-random-3">
              <img src={orangeGermImg} alt="Germ" className="img-fluid" />
            </div>
            <div className="germ germ-4 rotate delay-random-4">
              <img src={turquoiseGermImg} alt="Germ" className="img-fluid" />
            </div>
          </div>
          <div
            className="germs-container germs-container-2"
            ref={germContainer2El}
          >
            <div className="germ germ-1 rotate delay-random-5">
              <img src={lightBlueGermImg} alt="Germ" className="img-fluid" />
            </div>
            <div className="germ germ-2 rotate delay-random-6">
              <img src={orangeGerm2Img} alt="Germ" className="img-fluid" />
            </div>
            <div className="germ germ-3 rotate delay-random-7">
              <img src={darkBlueGermImg} alt="Germ" className="img-fluid" />
            </div>
            <div className="germ germ-4 rotate delay-random-8">
              <img src={purplegermImg} alt="Germ" className="img-fluid" />
            </div>
            <div className="germ germ-5">
              <img
                src={lightBlueGermImg}
                alt="Germ"
                className="img-fluid rotate delay-random-9"
              />
            </div>
          </div>
          <div className="bubble-container" ref={bubbleContainerEl}>
            <div className="bubble bubble-1 float delay-random-1">
              <img src={bubbleImg} alt="Bubble" className="img-fluid" />
            </div>
            <div className="bubble bubble-2 float delay-random-2">
              <img src={bubbleImg} alt="Bubble" className="img-fluid" />
            </div>
            <div className="bubble bubble-3 float delay-random-3">
              <img src={bubbleImg} alt="Bubble" className="img-fluid" />
            </div>
            <div className="bubble bubble-4 float delay-random-4">
              <img src={bubbleImg} alt="Bubble" className="img-fluid" />
            </div>
          </div>
        </div>
      </div>

      {/* STEPS */}
      {animationSequence.map(({ stepNumber, height, description, offset }) => {
        return (
          <Step
            stepSection={step}
            stepNumber={stepNumber}
            stepDescription={description}
            height={height}
            key={stepNumber}
            offset={offset}
          />
        );
      })}
    </section>
  );
}
